<!--
 * @Description: 自然人信息管理列表
 * @Date: 2022-09-19 11:03:48
-->
<template>
  <div style="padding: 14px 20px 20px">
    <XrkTable
      ref="TableRef"
      :search-config="searchConfig"
      :table-config="tableConfig"
      :get-list-ajax="getList"
    >
      <template #tableTop>
        <BaseTabs v-model="publishType" :disabled="checking">
          <BaseTabPane
            v-for="(value, key) in tableTypes"
            :key="key"
            :label="value"
            :name="+key"
          ></BaseTabPane>
        </BaseTabs>
      </template>
    </XrkTable>
  </div>
</template>

<script lang="tsx" setup>
import {
  XrkTable,
  XrkTableProps,
  BaseButton,
  XrkTableRef,
  useInputColumn,
  BaseTabs,
  BaseTabPane,
  createDialog,
  BaseInput,
  BaseForm,
  BaseFormItem,
  BaseFormRef,
  BaseCheckboxGroup,
  BaseCheckbox,
  BaseButtonProps,
  XrkMessage,
  BaseSelect,
  BaseToolTip,
  BaseIcon,
  BaseTextContent
} from 'xrk-components';
import { dealTableColumnstextLineClamp } from '../tools/index';
import { Zs } from '../data-source/types';
import { computed, onActivated, reactive, ref, watch } from 'vue';
import {
  PRESCRIPTION_TYPE,
  ROAD_TYPE,
  STATUS_TYPE,
  STATUS_TYPE_MAP,
  STATUS_AUDIT_VALUE,
  STATUS_REJECT_VALUE,
  STATUS_RELEASED_VALUE,
  STATUS_NO_CONFIRM_VALUE,
  STATUS_NO_RELEASED_VALUE,
  STATUS_SUSPEND_VALUE,
  MODIFY_STATUS_TYPE_MAP
} from '../config';
import ConfigDetailVue from '../components/config-detail/config-detail.vue';
import { cloneDeep } from 'lodash';
import {
  getConfigList,
  getConfig,
  getConfigCompanyList,
  setConfigStatus,
  setConfigTop
} from '../data-source/api';
import { createZsName } from '../router';
import { useRoute, useRouter } from 'vue-router';
import { useKeepAlive } from '../hooks/use-keep-alive';
import { Warning } from '@element-plus/icons-vue';

useKeepAlive([createZsName('config')]);
const route = useRoute();
const router = useRouter();

type TableConfig = XrkTableProps<Zs['search'], Zs['row']>;

const OSS_TYPE = 2;
const ENTERPRISE_TYPE = 1;

const tableTypes = {
  [ENTERPRISE_TYPE]: '企业发布',
  [OSS_TYPE]: '平台发布'
};
const publishType = ref<keyof typeof tableTypes>(
  ([1, 2].includes(+route.query.publishType)
    ? route.query.publishType
    : 1) as keyof typeof tableTypes
);
const checking = ref(false);

const createZsDetailDialog = (
  row: Zs['row'],
  type: 'audit' | 'perview',
  callback?: any
) => {
  const productData = cloneDeep(row.productList);
  const title = { audit: '审核', perview: '查看' }[type];
  const FailFormRef = ref<BaseFormRef>();
  const failFormData = reactive({
    reason: ''
  });
  const confirmBtnConfig = reactive<
    BaseButtonProps & { show: boolean; content: string }
  >({
    show: true,
    type: 'primary',
    content: '通过',
    loading: false
  });
  const cancelBtnConfig = reactive<
    BaseButtonProps & { show: boolean; content: string }
  >({
    show: true,
    type: 'danger',
    content: '驳回',
    loading: false
  });

  const setBtnLoading = (loading: boolean) => {
    cancelBtnConfig.loading = loading;
    confirmBtnConfig.loading = loading;
  };

  createDialog({
    title: title,
    width: 450,
    contentRender: () => (
      <>
        <BaseTextContent>
          <ConfigDetailVue
            perviewRole={type === 'audit' ? 'oss' : 'c'}
            productData={productData}
            tagValues={row.tagValues}
            content={row.introduce}
          ></ConfigDetailVue>
          {type === 'audit' && (
            <div
              style={{
                position: 'sticky',
                bottom: 0,
                background: '#fff',
                transform: 'translateY(25px)',
                paddingTop: '12px',
                paddingBottom: '18px',
                zIndex: 2
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                端口展示
              </div>
              <div
                style={{
                  border: '1px solid #e5e5e5',
                  borderRadius: '4px',
                  padding: '0 12px',
                  marginBottom: '12px'
                }}
              >
                <BaseCheckboxGroup disabled modelValue={row.platforms}>
                  {ROAD_TYPE.map(item => {
                    return (
                      <BaseCheckbox key={item.value} value={item.value}>
                        {item.label}
                      </BaseCheckbox>
                    );
                  })}
                </BaseCheckboxGroup>
              </div>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                驳回原因
              </div>
              <BaseForm
                model={failFormData}
                ref={FailFormRef}
                rules={{ reason: { required: true, message: '填写驳回原因' } }}
              >
                <BaseFormItem prop={'reason'} style={{ marginBottom: 0 }}>
                  <BaseInput
                    style={{ width: '100%' }}
                    type="textarea"
                    resize="none"
                    modelValue={failFormData.reason}
                    onInput={(val: string) => (failFormData.reason = val)}
                    maxlength={100}
                    showWordLimit
                    rows={2}
                    placeholder="请输入驳回原因（最多100字符长度）"
                  ></BaseInput>
                </BaseFormItem>
              </BaseForm>
            </div>
          )}
        </BaseTextContent>
      </>
    ),
    showClose: true,
    showFooter: type === 'audit',
    cancelBtnConfig: cancelBtnConfig,
    confirmBtnConfig: confirmBtnConfig,
    beforeCancel: async () => {
      // 驳回
      await FailFormRef.value?.validate();
      try {
        setBtnLoading(true);
        await setConfigStatus({
          configId: row.id,
          status: STATUS_REJECT_VALUE,
          reason: failFormData.reason
        });
        XrkMessage.success('操作成功');
      } catch (error) {
        console.log(error);
      } finally {
        setBtnLoading(false);
        callback?.();
      }
    },
    beforeConfirm: async () => {
      // 通过
      try {
        setBtnLoading(true);
        await setConfigStatus({
          configId: row.id,
          status: STATUS_RELEASED_VALUE,
          reason: failFormData.reason,
          otcList: productData.map(item => ({
            isOtc: item.isOtc,
            productId: item.productId
          }))
        });
        XrkMessage.success('操作成功');
      } catch (error) {
        console.log(error);
      } finally {
        setBtnLoading(false);
        callback?.();
      }
    }
  });
};

const { TableRef, initList, initCurrentList } = (() => {
  const TableRef = ref<XrkTableRef>();
  return {
    TableRef,
    initList: () => TableRef.value?.initList(),
    initCurrentList: () => TableRef.value?.initCurrentList()
  };
})();

const searchConfig = computed(() => {
  const config: TableConfig['searchConfig'] = {
    searchColumns: [
      {
        type: 'select',
        prop: 'enterpriseId',
        label: '企业名称',
        config: {
          options: PRESCRIPTION_TYPE,
          remoteMethod: async searchVal => {
            const res = await getConfigCompanyList({
              selectType: 2,
              keyword: searchVal,
              type: publishType.value
            });
            return res;
          },
          clearable: true
        }
      },
      useInputColumn({
        prop: 'phone',
        label: '手机号',
        config: {
          type: 'tel',
          maxlength: 11,
          clearable: true
        }
      }),
      {
        type: 'select',
        prop: 'shownPlatform',
        label: '展示端口',
        config: {
          options: ROAD_TYPE,
          clearable: true
        }
      },
      {
        type: 'select',
        prop: 'publishStatus',
        label: '状态',
        config: {
          init: true,
          remoteMethod: () => {
            const statusSet = new Set([
              STATUS_RELEASED_VALUE,
              STATUS_REJECT_VALUE,
              STATUS_SUSPEND_VALUE
            ]);
            if (publishType.value == ENTERPRISE_TYPE) {
              statusSet.add(STATUS_AUDIT_VALUE);
            }
            if (publishType.value == OSS_TYPE) {
              statusSet.add(STATUS_NO_RELEASED_VALUE);
              statusSet.add(STATUS_NO_CONFIRM_VALUE);
            }
            return {
              data: STATUS_TYPE.filter(({ value }) => statusSet.has(value))
            };
          },
          clearable: true
        }
      }
    ],
    otherComponents: []
  };
  if (publishType.value == OSS_TYPE) {
    config.otherComponents?.push({
      render: () => (
        <BaseButton
          type="primary2"
          onClick={() => {
            const formData = reactive({
              enterpriseId: '',
              enterpriseName: '',
              phone: ''
            });
            const FormRef = ref<BaseFormRef>();
            createDialog({
              width: 400,
              title: '创建招商',
              contentRender: () => (
                <BaseTextContent>
                  <BaseForm
                    ref={FormRef}
                    model={formData}
                    label-position="right"
                    rules={{
                      enterpriseId: [
                        { required: true, message: '请选择企业名称' }
                      ]
                    }}
                  >
                    <BaseFormItem label="企业名称" prop="enterpriseId" required>
                      <BaseSelect
                        placeholder="请选择企业名称"
                        style="width: 100%"
                        filterUseRemote
                        remoteMethod={(searchVal: string) =>
                          getConfigCompanyList({
                            selectType: 1,
                            keyword: searchVal || ''
                          })
                        }
                        modelValue={formData['enterpriseId']}
                        onChange={(val: string, searchValues: any) => {
                          formData['phone'] = searchValues[0]?.phone;
                          formData['enterpriseName'] = searchValues[0]?.label;
                          formData['enterpriseId'] = val;
                        }}
                      ></BaseSelect>
                    </BaseFormItem>
                    <BaseFormItem label="手机号" prop="phone">
                      <BaseInput
                        style="width: 100%"
                        disabled
                        modelValue={formData['phone']}
                      ></BaseInput>
                    </BaseFormItem>
                  </BaseForm>
                </BaseTextContent>
              ),
              beforeConfirm: async () => {
                await FormRef.value?.validate();
                router.push({
                  name: createZsName('config'),
                  query: {
                    enterpriseId: formData.enterpriseId,
                    enterpriseName: formData.enterpriseName
                  }
                });
              }
            });
          }}
        >
          创建招商
        </BaseButton>
      )
    });
  }
  return config;
});

const tableConfig = computed<TableConfig['tableConfig']>(() => {
  const config = {
    tableColumns: dealTableColumnstextLineClamp<
      TableConfig['tableConfig']['tableColumns']
    >([
      { type: 'index', label: '序号', width: 80 },
      { prop: 'enterpriseName', label: '企业名称' },
      { width: 140, prop: 'phone', label: '企业手机号' },
      { prop: 'productNames', label: '产品信息' },
      {
        minWidth: 200,
        prop: 'labelList',
        label: '能力标签',
        render: ({ cellValue }) => {
          if (Array.isArray(cellValue)) {
            return (
              <div style={{ textAlign: 'left' }}>
                {cellValue.map(tag => {
                  return (
                    <div class="table-tool-tip">
                      <BaseToolTip
                        popperClass="base-tool-tip-max-width-80vw"
                        trigger="hover"
                        overShow
                        content={`${tag.label}：${tag.options}`}
                        style={`
                      overflow: hidden;
                      text-overflow: ellipsis;
                      -webkit-box-orient: vertical;
                      display: -webkit-box;
                      -webkit-line-clamp: 2;
                      white-space: initial;
                      word-break: break-all;`}
                      >
                        <b>{tag.label}</b>：{tag.options}
                      </BaseToolTip>
                    </div>
                  );
                })}
              </div>
            );
          }
          return <>{cellValue}</>;
        }
      },
      { prop: 'shownPlatform', label: '展示端口' },
      {
        width: 100,
        prop: 'publishStatus',
        label: '招商发布状态',
        render: ({ cellValue, row }) => {
          return (
            <p style="display:flex;align-items:center;gap:4px;">
              <span>
                {STATUS_TYPE_MAP[cellValue as Zs['row']['publishStatus']] ||
                  '-'}
              </span>
              {row.zsIsAudit != 1 && publishType.value == ENTERPRISE_TYPE && (
                <BaseIcon color="red">
                  <Warning></Warning>
                </BaseIcon>
              )}
            </p>
          );
        }
      },
      {
        width: 100,
        prop: 'modifyStatus',
        label: '招商修改状态',
        render: ({ cellValue }) => {
          return (
            <p style="display:flex;align-items:center;gap:4px;">
              <span>
                {MODIFY_STATUS_TYPE_MAP[
                  cellValue as Zs['row']['modifyStatus']
                ] || '-'}
              </span>
            </p>
          );
        }
      },
      {
        width: 160,
        label: '操作',
        render: ({ row }) => {
          const { publishStatus } = row;
          if (publishType.value == ENTERPRISE_TYPE) {
            if (
              [STATUS_AUDIT_VALUE, STATUS_REJECT_VALUE].includes(publishStatus)
            ) {
              return (
                <>
                  <BaseButton type="text" disabled={true}>
                    {'置顶'}
                  </BaseButton>
                  <BaseButton
                    disabled={publishStatus === STATUS_REJECT_VALUE}
                    type="text"
                    click={async () => {
                      const { data } = await getConfig({ configId: row.id });
                      createZsDetailDialog(
                        { ...data, tagValues: row.labelList as any },
                        'audit',
                        initCurrentList
                      );
                    }}
                  >
                    审核
                  </BaseButton>
                </>
              );
            }
            return (
              <>
                <BaseButton
                  type="text"
                  disabled={row.publishStatus != STATUS_RELEASED_VALUE}
                  click={async () => {
                    const targetTop = row.isTop ? 0 : 1;
                    await setConfigTop({
                      configId: row.id,
                      isTop: targetTop
                    });
                    initCurrentList();
                    XrkMessage.success('操作成功');
                  }}
                >
                  {row.isTop ? '取消置顶' : '置顶'}
                </BaseButton>
                <BaseButton
                  type="text"
                  click={async () => {
                    const { data } = await getConfig({ configId: row.id });
                    createZsDetailDialog(
                      { ...data, tagValues: row.labelList as any },
                      'perview'
                    );
                  }}
                >
                  查看
                </BaseButton>
              </>
            );
          }
          return (
            <>
              <BaseButton
                type="text"
                disabled={row.publishStatus != STATUS_RELEASED_VALUE}
                click={async () => {
                  const targetTop = row.isTop ? 0 : 1;
                  await setConfigTop({
                    configId: row.id,
                    isTop: targetTop
                  });
                  initCurrentList();
                  XrkMessage.success('操作成功');
                }}
              >
                {row.isTop ? '取消置顶' : '置顶'}
              </BaseButton>
              <BaseButton
                type="text"
                onClick={() => {
                  router.push({
                    name: createZsName('config'),
                    query: {
                      enterpriseId: row.enterpriseId,
                      enterpriseName: row.enterpriseName,
                      configId: row.id
                    }
                  });
                }}
              >
                编辑
              </BaseButton>
            </>
          );
        }
      }
    ])
  };
  return config;
});

const getList: TableConfig['getListAjax'] = async params => {
  return (await getConfigList({
    ...params,
    type: publishType.value
  })) as any;
};

watch(
  () => publishType.value,
  async () => {
    router.replace({
      query: {
        ...(route.query || {}),
        publishType: publishType.value
      }
    });
    checking.value = true;
    TableRef.value?.clearSearchValues();
    await initList();
    checking.value = false;
  }
);

onActivated(() => {
  initCurrentList?.();
});
</script>

<style lang="scss" scoped></style>
